/**
 * NexoSyncRoute - Syncs the React Router pathname with the Nuvemshop admin URL bar.
 *
 * Drop inside a BrowserRouter. It does two things:
 * 1. On every route change, calls syncPathname so the admin URL bar stays in sync.
 * 2. Listens for ACTION_NAVIGATE_SYNC so admin-driven navigations update the app.
 *
 * Note: nexo.suscribe is the canonical spelling (not subscribe) - intentional SDK typo.
 */

import type { NavigateSyncResponse } from '@tiendanube/nexo';
import { ACTION_NAVIGATE_SYNC, syncPathname } from '@tiendanube/nexo';
import { type ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import nexoClient from '@/lib/nexo-client';

interface Props {
  children: ReactNode;
}

export function NexoSyncRoute({ children }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Push the app path to the admin URL bar on every navigation
  useEffect(() => {
    syncPathname(nexoClient, pathname);
  }, [pathname]);

  // Receive admin-driven navigations (initial route + subsequent pushes)
  useEffect(() => {
    const unsuscribe = nexoClient.suscribe(
      ACTION_NAVIGATE_SYNC,
      ({ path, replace }: NavigateSyncResponse) => {
        navigate(path, { replace: Boolean(replace) });
      },
    );
    return unsuscribe;
  }, [navigate]);

  return <>{children}</>;
}
